import { z } from 'zod';
import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useMutation } from '@tanstack/react-query';
import { passwordValidationSchema } from '@lili/lili-frontend-common';
import { useFormHook } from '@Hooks/index';
import { OnboardingFormFields } from '@Types/index';
import { HttpService } from '@Services/index';
import { useBoundStore } from '@Store/useBoundStore';
import { useNavigationManager } from '@Router/useNavigationManager';
import { RoutesMap } from '@Router/router.types';

const useSetPassword = () => {
  const { customerId, token } = useBoundStore((state) => state.resetPasswordSlice);

  return useMutation({
    mutationFn: async (password: string) => {
      return await HttpService.post(`v1/onboarding/user/openauth/${customerId}/recovery/password`, {
        setPasswordData: {
          password,
        },
        token,
      });
    },
  });
};

export const useRecoveryPasswordLogic = () => {
  const [isLengthValid, setIsLengthValid] = useState(false);
  const [isAtLeastOneCapital, setIsAtLeastOneCapital] = useState(false);
  const [isAtLeastOneNumber, setIsAtLeastOneNumber] = useState(false);
  const [isAtLeastOneLowerCase, setIsAtLeastOneLowerCase] = useState(false);
  const { mutateAsync: asyncSetPassword } = useSetPassword();
  const { navigateToNextPage } = useNavigationManager();
  const { formMethods } = useFormHook({
    formSchema: z.object({
      [OnboardingFormFields.PASSWORD]: passwordValidationSchema,
    }),
    formDefaultValues: {
      [OnboardingFormFields.PASSWORD]: '',
    },
    schemaTitle: 'Recovery Password',
  });

  // Watch password input and update flags
  const password = formMethods.watch(OnboardingFormFields.PASSWORD);

  useEffect(() => {
    setIsLengthValid(password.length >= 8);
    setIsAtLeastOneCapital(/[A-Z]/.test(password));
    setIsAtLeastOneNumber(/[0-9]/.test(password));
    setIsAtLeastOneLowerCase(/[a-z]/.test(password));
  }, [password]);

  const hintStyles = twMerge(
    !formMethods.getValues(OnboardingFormFields.PASSWORD) ||
      Object.values(formMethods.formState.errors).length > 0
      ? 'mt-[16px]'
      : 'mt-[32px]',
    'flex flex-col items-start w-full gap-[10px] transition-all duration-300'
  );

  const handleSubmit = async () => {
    try {
      await asyncSetPassword(password);
      navigateToNextPage(RoutesMap.RECOVERY_SET_PASSWORD);
    } catch (error) {
      console.log(error);
    }
  };

  return {
    formMethods,
    isLengthValid,
    isAtLeastOneCapital,
    isAtLeastOneNumber,
    isAtLeastOneLowerCase,
    hintStyles,
    handleSubmit,
  };
};
